/**
 * `skillPromptSections.ts` — pure formatters for the two progressive-disclosure prompt sections.
 *
 * Unity hosts these on `AIInterfaceModel` (`BuildAvailableSkillsIndexSection` /
 * `BuildLoadedSkillsPromptSection`); on web the format lives here so it stays locked to Unity's
 * exact copy while the interface model supplies the skill state. The section text is
 * parity-critical — both clients' models must see identical prompts.
 */

import type { AIRealtimeSkillDefinition } from "./aiRealtimeContentTypes";

/**
 * Builds the always-present compact skill index for the active role. Returns null when no skill
 * qualifies. Mirrors `AIInterfaceModel.BuildAvailableSkillsIndexSection`.
 */
/**
 * @param isInRoleScope whether the skill is reachable from the ACTIVE role. A
 * predicate rather than the role id it used to take: the answer is no longer a
 * property of the definition alone — the account can add or withdraw a role for
 * a skill (`AIInterfaceModel.isSkillInRoleScope`) — and asking `appliesToRole`
 * here would quietly undo an addition on the one surface that decides what the
 * model is told a skill exists.
 */
export function buildAvailableSkillsIndexSection(
  availableSkills: readonly AIRealtimeSkillDefinition[],
  isInRoleScope: (skill: AIRealtimeSkillDefinition) => boolean,
  isSkillLoaded: (skillId: string) => boolean,
): string | null {
  if (availableSkills.length === 0) {
    return null;
  }

  const entries: string[] = [];
  for (const skill of availableSkills) {
    if (!skill || !skill.id.trim()) {
      continue;
    }

    // Only advertise skills available in the active role.
    if (!isInRoleScope(skill)) {
      continue;
    }

    // Skills already loaded appear in full under "# Loaded Skills"; no need to re-advertise them.
    if (isSkillLoaded(skill.id)) {
      continue;
    }

    const summary = skill.description?.trim() ? skill.description.trim() : skill.displayName;
    entries.push(`- ${skill.id}: ${summary}`);
  }

  if (entries.length === 0) {
    return null;
  }

  const sections = [
    "# Available Skills",
    "- These optional skills provide extra step-by-step guidance for specific tasks. They are NOT active yet.",
    "- When the user's request matches a skill below, call Load_Skill with its skill_id BEFORE acting. The tool returns the full guidance and keeps it active for the rest of the session.",
    "- Do NOT mention skills, skill ids, or Load_Skill to the user; just apply the guidance naturally.",
    entries.join("\n"),
  ];

  return sections.join("\n");
}

/**
 * Builds the full-body section for skills the model has loaded. Returns null when no loaded skill
 * carries markdown. Mirrors `AIInterfaceModel.BuildLoadedSkillsPromptSection`.
 */
export function buildLoadedSkillsPromptSection(
  loadedSkills: readonly AIRealtimeSkillDefinition[],
): string | null {
  if (loadedSkills.length === 0) {
    return null;
  }

  // "StreamingAssets/AI/skills" is kept verbatim from Unity for prompt parity (the wording is
  // inconsequential to the model; on web the files ship under public/AI/skills).
  const sections: string[] = [
    "# Loaded Skills",
    "- The user explicitly loaded the following skills from StreamingAssets/AI/skills. Apply them in addition to the base system instructions.",
  ];

  for (const skill of loadedSkills) {
    if (!skill || !skill.markdown.trim()) {
      continue;
    }

    sections.push(`## ${skill.displayName}\nSource: skills/${skill.fileName}\n\n${skill.markdown.trim()}`);
  }

  return sections.length <= 2 ? null : sections.join("\n\n");
}
